import { useCallback, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

type ParamValue = string | number | boolean

export type URLSearchParamsInit =
  | URLSearchParams
  | string
  | string[][]
  | Record<string, ParamValue | ParamValue[] | null | undefined>

export type NavigateOptions = {
  replace?: boolean
  state?: unknown
}

type SetSearchParams = (
  nextInit: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
  navigateOptions?: NavigateOptions,
) => void

const toSearchParams = (init?: URLSearchParamsInit): URLSearchParams => {
  if (!init) {
    return new URLSearchParams()
  }

  if (
    typeof init === 'string' ||
    Array.isArray(init) ||
    init instanceof URLSearchParams
  ) {
    return new URLSearchParams(init)
  }

  const searchParams = new URLSearchParams()

  Object.entries(init).forEach(([key, value]) => {
    if (value === null || typeof value === 'undefined') {
      return
    }

    if (Array.isArray(value)) {
      value.forEach(item => {
        searchParams.append(key, String(item))
      })

      return
    }

    searchParams.set(key, String(value))
  })

  return searchParams
}

export const useSearchParams = (
  defaultInit?: URLSearchParamsInit,
): [URLSearchParams, SetSearchParams] => {
  const history = useHistory()
  const location = useLocation()
  const defaultSearchParams = useRef(toSearchParams(defaultInit))
  const hasSetSearchParamsRef = useRef(false)

  const withDefaultSearchParams = useCallback((params: URLSearchParams) => {
    const nextParams = toSearchParams(params)

    defaultSearchParams.current.forEach((value, key) => {
      if (!nextParams.has(key)) {
        nextParams.append(key, value)
      }
    })

    return nextParams
  }, [])

  const searchParams = useMemo(() => {
    const currentSearchParams = toSearchParams(location.search)
    return hasSetSearchParamsRef.current
      ? currentSearchParams
      : withDefaultSearchParams(currentSearchParams)
  }, [location.search, withDefaultSearchParams])

  const setSearchParams = useCallback<SetSearchParams>(
    (nextInit, navigateOptions) => {
      const currentSearchParams = hasSetSearchParamsRef.current
        ? toSearchParams(history.location.search)
        : withDefaultSearchParams(toSearchParams(history.location.search))
      hasSetSearchParamsRef.current = true
      const resolvedNextInit =
        typeof nextInit === 'function' ? nextInit(currentSearchParams) : nextInit
      const nextSearchParams = toSearchParams(resolvedNextInit)
      const search = nextSearchParams.toString()
      const nextLocation = {
        pathname: history.location.pathname,
        hash: history.location.hash,
        search: search ? `?${search}` : '',
        state: navigateOptions?.state,
      }

      if (navigateOptions?.replace) {
        history.replace(nextLocation)
      } else {
        history.push(nextLocation)
      }
    },
    [history, withDefaultSearchParams],
  )

  return [searchParams, setSearchParams]
}
